import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, DialogTrigger } from '@fluentui/react-components'
import FormContainer from '../../../../components/Form/FormContainer'
import { Message } from '@microsoft/microsoft-graph-types'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { useFormikContext } from 'formik'
import { HammockFormDialogProps } from '../HammockFormDialog'
import * as Yup from 'yup'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { FormikConfig } from 'formik/dist/types'
import { IFileDto } from '../../../../config/fileConfig'
import { Destination } from '../../../../context/AxiosContext'
import useSharePoint from '../../../../hooks/useSharePoint'
import FilePicker, { FilePickerProps } from '../../../../components/TagPicker/FilePicker'
import useProcessEmail from '../../../../hooks/email/useProcessEmail'
import { EmailProcessingAction } from '../../../../components/Dialog/ProcessEmail/ProcessEmailForm'

export const getMutateMessageDialogBodySchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        references: Yup.array().required(translate('file.pivot.email.mutate.form.references.required')),
    })
}

export type MutateMessageDialogBodyProps = {
    items?: Message[]
    action?: EmailProcessingAction
    file?: IFileDto
    destination: Destination
    userEmail: string
    notificationEmail: string
    callback?: () => void
}

export type MutateMessageDialogBodyValues = {
    references: string[]
}

export default function MutateMessageDialogBody(
    props: HammockFormDialogProps<MutateMessageDialogBodyValues, MutateMessageDialogBodyProps>,
): ReactElement {
    const { sharePoint } = useSharePoint()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const { isSubmitting } = useFormikContext<MutateMessageDialogBodyValues>()

    const dialogFormBodyProps = useMemo(() => props.dialogFormBodyProps() ?? ({} as MutateMessageDialogBodyProps), [props])
    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const titleKey = useMemo(() => {
        if (dialogFormBodyProps.action === EmailProcessingAction.Copy) {
            return dialogFormBodyProps.items.length > 1 ? `file.pivot.email.mutate.copy.title.multiple` : `file.pivot.email.mutate.copy.title.single`
        } else if (dialogFormBodyProps.action === EmailProcessingAction.Move) {
            return dialogFormBodyProps.items.length > 1 ? `file.pivot.email.mutate.move.title.multiple` : `file.pivot.email.mutate.move.title.single`
        }
        return null
    }, [])

    const { processSelection } = useProcessEmail()

    const handleSubmit = useCallback<FormikConfig<MutateMessageDialogBodyValues>['onSubmit']>(
        values => {
            processSelection(
                dialogFormBodyProps.userEmail,
                dialogFormBodyProps.notificationEmail,
                dialogFormBodyProps.items,
                oldReferences => {
                    if (dialogFormBodyProps.action === EmailProcessingAction.Move) {
                        return [...new Set(values.references)]
                    } else if (dialogFormBodyProps.action === EmailProcessingAction.Copy) {
                        return [...new Set(oldReferences.concat(values.references))]
                    } else if (dialogFormBodyProps.action === EmailProcessingAction.File) {
                        return [...new Set(values.references)]
                    } else if (dialogFormBodyProps.action === EmailProcessingAction.Ignore) {
                        return []
                    } else {
                        return [...new Set(values.references)]
                    }
                },
                () => {},
                () => {},
            ).then(() => dialogFormBodyProps.callback?.())
        },
        [sharePoint],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    return (
        <DialogBody>
            <DialogTitle>{translate(titleKey)}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    <FormikComboboxField<MutateMessageDialogBodyValues, FilePickerProps>
                        required
                        labelKey={'file.pivot.email.mutate.form.references'}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                        fieldName={'references'}
                        component={FilePicker}
                        componentProps={{
                            getEntityValue: file => file.reference,
                        }}
                    />
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button appearance="primary" type={'submit'} disabled={isSubmitting}>
                    {translate(`file.pivot.email.mutate.${dialogFormBodyProps.action.toLowerCase()}.button`)}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary" disabled={isSubmitting}>
                        {translate('action.cancel')}
                    </Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
